

import { useState, useEffect } from "react";
import { database, auth } from "../firebase";
import { ref, onValue, off } from "firebase/database";
import { onAuthStateChanged } from "firebase/auth";
import ReactDOMServer from "react-dom/server";

// ----------- Utility & Filter Section -----------

/** Extract city from an address */
const getCityFromAddress = (address) => {
  if (!address) return "Unknown City";
  const addressParts = address.trim().split(/\s+/);
  return addressParts.length > 0
    ? addressParts[addressParts.length - 1]
    : "Unknown City";
};

/** Checkbox multi-select filter block */
const FilterSection = ({ title, options, selected, onChange }) => {
  const handleCheckboxChange = (option) => {
    if (option === options[0]) {
      if (selected.includes(option)) {
        onChange([]);
      } else {
        onChange([...options]);
      }
    } else {
      const newSelected = selected.includes(option)
        ? selected.filter((item) => item !== option && item !== options[0])
        : [...selected.filter((item) => item !== options[0]), option];
      if (newSelected.length === options.length - 1) {
        onChange([options[0], ...newSelected]);
      } else {
        onChange(newSelected);
      }
    }
  };

  return (
    <div
      style={{
        backgroundColor: "#f8fafc",
        padding: "16px",
        borderRadius: "8px",
        border: "1px solid #e2e8f0",
        boxShadow: "0 2px 4px rgba(0, 0, 0, 0.05)",
        transition: "all 0.3s ease",
        maxWidth: "220px",
        marginBottom: "16px",
      }}
    >
      <h2
        style={{
          fontSize: "16px",
          fontWeight: "600",
          color: "#1e293b",
          marginBottom: "8px",
          borderBottom: "2px solid #3b82f6",
          paddingBottom: "6px",
        }}
      >
        {title}
      </h2>
      <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
        {options.map((option) => (
          <label
            key={option}
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              cursor: "pointer",
              padding: "6px",
              borderRadius: "6px",
              transition: "background-color 0.2s",
              backgroundColor: selected.includes(option)
                ? "#eff6ff"
                : "transparent",
            }}
            onMouseEnter={(e) => {
              if (!selected.includes(option)) {
                e.currentTarget.style.backgroundColor = "#f1f5f9";
              }
            }}
            onMouseLeave={(e) => {
              if (!selected.includes(option)) {
                e.currentTarget.style.backgroundColor = "transparent";
              }
            }}
          >
            <input
              type="checkbox"
              checked={selected.includes(option)}
              onChange={() => handleCheckboxChange(option)}
              style={{
                height: "14px",
                width: "14px",
                accentColor: "#3b82f6",
                cursor: "pointer",
              }}
            />
            <span
              style={{
                fontSize: "14px",
                color: "#475569",
                userSelect: "none",
                fontWeight: selected.includes(option) ? "500" : "400",
              }}
            >
              {option}
            </span>
          </label>
        ))}
      </div>
    </div>
  );
};

// ----------- Display Component for Sales Customer Return Report HSN -----------

const SalesCustomerReturnReportHSNDisplay = ({
  formData,
  filters,
  branchProfile,
  returnData,
  customerMap = {},
}) => {
  // Helper for Date Formatting
  const parseReturnDate = (dateString) => {
    if (!dateString) return null;
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
      const parts = dateString.split("-");
      return new Date(
        Number(parts[0]),
        Number(parts[1]) - 1,
        Number(parts[2])
      );
    }
    if (/^\d{2}[-/]\d{2}[-/]\d{4}$/.test(dateString)) {
      const parts = dateString.split(/[-/]/);
      return new Date(Number(parts[2]), Number(parts[1]) - 1, Number(parts[0]));
    }
    return new Date(dateString);
  };
  const formatDisplayDate = (dateString) => {
    const date = parseReturnDate(dateString);
    return !date || isNaN(date.getTime())
      ? "N/A"
      : date.toLocaleDateString("en-GB", {
          day: "2-digit",
          month: "2-digit",
          year: "numeric",
        });
  };

  // Filter Data by UI Selections
  let filteredReturnData = returnData;

  if (formData.fromDate && formData.toDate) {
    const fromDate = new Date(formData.fromDate);
    const toDate = new Date(formData.toDate);
    toDate.setHours(23, 59, 59, 999);
    filteredReturnData = filteredReturnData.filter((item) => {
      const itemDate = parseReturnDate(item.srnDate);
      return (
        itemDate && !isNaN(itemDate.getTime()) && itemDate >= fromDate && itemDate <= toDate
      );
    });
  }
  if (!filters.store.includes("All Stores")) {
    filteredReturnData = filteredReturnData.filter((item) =>
      filters.store.includes(item.store)
    );
  }
  if (!filters.customer.includes("All Customers")) {
    filteredReturnData = filteredReturnData.filter((item) =>
      filters.customer.includes(item.customer)
    );
  }
  if (!filters.group.includes("All Group Names")) {
    filteredReturnData = filteredReturnData.filter((item) =>
      filters.group.includes(item.groupName)
    );
  }
  if (!filters.product.includes("All Products")) {
    filteredReturnData = filteredReturnData.filter((item) =>
      filters.product.includes(item.itemDescription)
    );
  }

// Updated data processing in SalesCustomerReturnReportHSNDisplay
// Ensure correct Phone/TIN using customerMap, fallback to row if not mapped
let displayData = [];
if (formData.reportType === "abstract") {
  const groupKeyMap = {
    customerwise: 'customer',
    groupwise: 'groupName',
    datewise: 'srnDate',
  };
  const groupKey = groupKeyMap[formData.searchBy] || 'customer';
  const grouped = filteredReturnData.reduce((acc, item) => {
    let key = item[groupKey];
    if (groupKey === 'srnDate') {
      key = formatDisplayDate(key);
    }
    if (!acc[key]) {
      acc[key] = {
        key,
        gstAmount: 0,
        discAmount: 0,
        grossAmount: 0,
        netAmount: 0,
        customer: (groupKey === 'customer') ? key : key, // Use key for non-customer
        phoneNumber: "N/A",
        tin: "N/A",
      };
      if (groupKey === 'customer') {
        const cust = customerMap[key] || {};
        acc[key].phoneNumber = cust.phoneNo || item.phoneNumber || "N/A";
        acc[key].tin = cust.gstTinNo || item.tin || "N/A";
      }
    }
    acc[key].gstAmount += (Number(item.cgstTax) + Number(item.sgstTax)) || 0;
    acc[key].discAmount += Number(item.discount) || 0;
    acc[key].grossAmount += Number(item.grossAmount) || 0;
    acc[key].netAmount += Number(item.netAmount) || 0;
    return acc;
  }, {});
  displayData = Object.values(grouped);
  displayData.sort((a, b) => a.key.localeCompare(b.key));
} else {
  displayData = filteredReturnData.map((item) => {
    let phone = item.phoneNumber || "N/A";
    let tin = item.tin || "N/A";
    if (customerMap[item.customer]) {
      const cust = customerMap[item.customer];
      phone = cust.phoneNo || phone;
      tin = cust.gstTinNo || tin;
    }
    return {
      ...item,
      phoneNumber: phone,
      tin,
      invNo: item.srnNo || "N/A",
      invDate: item.srnDate || "N/A",
      gstAmount: (Number(item.cgstTax) || 0) + (Number(item.sgstTax) || 0),
      discAmount: item.discount || 0,
      grossAmount: item.grossAmount || 0,
      netAmount: item.netAmount || 0,
    };
  });
  displayData.sort((a, b) => {
    return (
      a.customer.localeCompare(b.customer) ||
      parseReturnDate(a.srnDate) - parseReturnDate(b.srnDate)
    );
  });
}

// Updated totals
const totalGstAmount = displayData.reduce(
  (sum, item) => sum + (Number(item.gstAmount) || 0),
  0
);
const totalDiscAmount = displayData.reduce(
  (sum, item) => sum + (Number(item.discAmount) || 0),
  0
);
const totalGrossAmount = displayData.reduce(
  (sum, item) => sum + (Number(item.grossAmount) || 0),
  0
);
const totalNetAmount = displayData.reduce(
  (sum, item) => sum + (Number(item.netAmount) || 0),
  0
);

// Updated renderTableHeaders
const renderTableHeaders = () => {
  if (formData.reportType === "abstract") {
    return (
      <tr style={{ backgroundColor: "#f1f5f9", borderBottom: "2px solid #3b82f6" }}>
        <th style={thStyle}>S.No</th>
        <th style={thStyle}>{formData.searchBy.replace('wise', '').charAt(0).toUpperCase() + formData.searchBy.replace('wise', '').slice(1)}</th>
        {formData.searchBy === "customerwise" && <th style={thStyle}>Phone Number</th>}
        {formData.searchBy === "customerwise" && <th style={thStyle}>TIN</th>}
        <th style={thStyle}>GST Amnt</th>
        <th style={thStyle}>Disc Amount</th>
        <th style={thStyle}>Gross Amount</th>
        <th style={thStyle}>NetAmount</th>
      </tr>
    );
  } else {
    return (
      <tr style={{ backgroundColor: "#f1f5f9", borderBottom: "2px solid #3b82f6" }}>
        <th style={thStyle}>S.No</th>
        <th style={thStyle}>Customer Name</th>
        <th style={thStyle}>Phone Number</th>
        <th style={thStyle}>TIN</th>
        <th style={thStyle}>Inv.No</th>
        <th style={thStyle}>Inv.Date</th>
        <th style={thStyle}>GST Amnt</th>
        <th style={thStyle}>Disc Amount</th>
        <th style={thStyle}>Gross Amount</th>
        <th style={thStyle}>NetAmount</th>
      </tr>
    );
  }
};

// Updated renderTableRows
const renderTableRows = () => {
  if (displayData.length === 0) {
    const colSpan = formData.reportType === "abstract" ? (formData.searchBy === "customerwise" ? 8 : 6) : 10;
    return (
      <tr>
        <td
          colSpan={colSpan}
          style={{
            border: "1px solid #cbd5e1",
            padding: "30px",
            textAlign: "center",
            color: "#64748b",
            fontStyle: "italic",
            fontSize: "14px",
            backgroundColor: "#f8fafc",
          }}
        >
          No return data found for the selected criteria
        </td>
      </tr>
    );
  }
  return displayData.map((item, idx) => (
    <tr
      key={`${item.key || item.customer}-${idx}`}
      style={{ backgroundColor: idx % 2 === 0 ? "#ffffff" : "#f8fafc" }}
    >
      <td style={tdStyleCenter}>{idx + 1}</td>
      <td style={tdStyleLeft}>{item.customer}</td>
      {formData.reportType === "abstract" && formData.searchBy !== "customerwise" ? null : <td style={tdStyleCenter}>{item.phoneNumber}</td>}
      {formData.reportType === "abstract" && formData.searchBy !== "customerwise" ? null : <td style={tdStyleCenter}>{item.tin}</td>}
      {formData.reportType === "details" && <td style={tdStyleCenter}>{item.invNo}</td>}
      {formData.reportType === "details" && <td style={tdStyleCenter}>{formatDisplayDate(item.invDate)}</td>}
      <td style={tdStyleRight}>{Number(item.gstAmount).toFixed(2)}</td>
      <td style={tdStyleRight}>{Number(item.discAmount).toFixed(2)}</td>
      <td style={tdStyleRight}>{Number(item.grossAmount).toFixed(2)}</td>
      <td style={tdStyleRight}>{Number(item.netAmount).toFixed(2)}</td>
    </tr>
  ));
};

// Updated renderTotalRow and renderGrandTotalRow to adjust colSpan
const renderTotalRow = () => {
  if (displayData.length === 0) return null;
  const colSpan = formData.reportType === "abstract" ? (formData.searchBy === "customerwise" ? 4 : 2) : 6;
  return (
    <tr style={{ backgroundColor: "#fef3c7", fontWeight: "bold", borderTop: "2px solid #f59e0b" }}>
      <td colSpan={colSpan} style={tdTotalLabel}>
        Total
      </td>
      <td style={tdTotalNum}>{totalGstAmount.toFixed(2)}</td>
      <td style={tdTotalNum}>{totalDiscAmount.toFixed(2)}</td>
      <td style={tdTotalNum}>{totalGrossAmount.toFixed(2)}</td>
      <td style={tdTotalNum}>{totalNetAmount.toFixed(2)}</td>
    </tr>
  );
};
const renderGrandTotalRow = () => {
  if (displayData.length === 0) return null;
  const colSpan = formData.reportType === "abstract" ? (formData.searchBy === "customerwise" ? 4 : 2) : 6;
  return (
    <tr style={{ backgroundColor: "#fee2e2", fontWeight: "bold", borderTop: "2px solid #dc2626" }}>
      <td colSpan={colSpan} style={tdTotalLabel}>
        Grand Total
      </td>
      <td style={tdTotalNum}>{totalGstAmount.toFixed(2)}</td>
      <td style={tdTotalNum}>{totalDiscAmount.toFixed(2)}</td>
      <td style={tdTotalNum}>{totalGrossAmount.toFixed(2)}</td>
      <td style={tdTotalNum}>{totalNetAmount.toFixed(2)}</td>
    </tr>
  );
};

  const city = getCityFromAddress(branchProfile.address);
  

  // Styles for table
  const thStyle = {
    border: "1px solid #cbd5e1",
    padding: "12px",
    textAlign: "center",
    fontWeight: "600",
    fontSize: "12px",
    background: "#f8fafc",
  };
  const tdStyleCenter = {
    border: "1px solid #cbd5e1",
    padding: "8px",
    textAlign: "center",
    fontSize: "11px",
  };
  const tdStyleLeft = {
    border: "1px solid #cbd5e1",
    padding: "8px",
    textAlign: "left",
    fontSize: "11px",
  };
  const tdStyleRight = {
    border: "1px solid #cbd5e1",
    padding: "8px",
    textAlign: "right",
    fontSize: "11px",
  };
  const tdTotalLabel = {
    border: "1px solid #cbd5e1",
    padding: "10px",
    textAlign: "center",
    fontSize: "12px",
    fontWeight: "600",
  };
  const tdTotalNum = {
    border: "1px solid #cbd5e1",
    padding: "10px",
    textAlign: "right",
    fontSize: "12px",
    fontWeight: "600",
  };

  return (
    <div
      style={{
        backgroundColor: "white",
        padding: "30px",
        marginTop: "20px",
        border: "2px solid #3b82f6",
        borderRadius: "12px",
        fontFamily: "Arial, sans-serif",
        fontSize: "12px",
        pageBreakInside: "avoid",
        boxShadow: "0 10px 15px -3px rgba(0, 0, 0, 0.1)",
      }}
      className="report-display"
    >
      {/* Header */}
      <div
        style={{
          textAlign: "center",
          marginBottom: "30px",
          paddingBottom: "20px",
          borderBottom: "2px solid #e2e8f0",
        }}
      >
        <h1
          style={{
            fontSize: "24px",
            fontWeight: "bold",
            margin: "0",
            marginBottom: "5px",
            color: "#1e293b",
            textShadow: "1px 1px 2px rgba(0,0,0,0.1)",
          }}
        >
          {branchProfile.branchName}
        </h1>
        <div
          style={{
            fontSize: "16px",
            fontWeight: "600",
            color: "#3b82f6",
            marginBottom: "10px",
          }}
        >
          {city}
        </div>
        <div style={{ fontSize: "14px", color: "#475569", marginBottom: "10px" }}>
          Sales Customer Return Report From :{" "}
          {formData.fromDate
            ? new Date(formData.fromDate).toLocaleDateString("en-GB")
            : "N/A"}{" "}
          To :{" "}
          {formData.toDate
            ? new Date(formData.toDate).toLocaleDateString("en-GB")
            : "N/A"}
        </div>
        <div style={{ fontSize: "14px", fontWeight: "600", color: "#1e293b" }}>
          Option: {formData.reportType === "details" ? "Detail" : "Abstract"}
        </div>
      </div>

      {/* Table */}
      <div style={{ overflowX: "auto" }}>
        <table
          style={{
            width: "100%",
            borderCollapse: "collapse",
            fontSize: "11px",
            border: "2px solid #3b82f6",
            borderRadius: "8px",
            overflow: "hidden",
            boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
          }}
        >
          <thead>{renderTableHeaders()}</thead>
          <tbody>
            {renderTableRows()}
            {renderTotalRow()}
            {renderGrandTotalRow()}
          </tbody>
        </table>
      </div>
      {/* Summary */}
      {displayData.length > 0 && (
        <div
          style={{
            marginTop: "30px",
            padding: "20px",
            backgroundColor: "#f8fafc",
            borderRadius: "8px",
            border: "1px solid #e2e8f0",
          }}
        >
          <h3
            style={{
              fontSize: "16px",
              fontWeight: "600",
              marginBottom: "15px",
              color: "#1e293b",
            }}
          >
            Summary
          </h3>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
              gap: "15px",
            }}
          >
            <div style={{ textAlign: "center" }}>
              <div style={{ fontSize: "12px", color: "#64748b" }}>Total Records</div>
              <div
                style={{
                  fontSize: "18px",
                  fontWeight: "600",
                  color: "#3b82f6",
                }}
              >
                {displayData.length}
              </div>
            </div>
            <div style={{ textAlign: "center" }}>
              <div style={{ fontSize: "12px", color: "#64748b" }}>Total GST Amount</div>
              <div
                style={{
                  fontSize: "18px",
                  fontWeight: "600",
                  color: "#059669",
                }}
              >
                {totalGstAmount.toFixed(2)}
              </div>
            </div>
            <div style={{ textAlign: "center" }}>
              <div style={{ fontSize: "12px", color: "#64748b" }}>Total Discount</div>
              <div
                style={{
                  fontSize: "18px",
                  fontWeight: "600",
                  color: "#dc2626",
                }}
              >
                {totalDiscAmount.toFixed(2)}
              </div>
            </div>
            <div style={{ textAlign: "center" }}>
              <div style={{ fontSize: "12px", color: "#64748b" }}>Gross Amount</div>
              <div
                style={{
                  fontSize: "18px",
                  fontWeight: "600",
                  color: "#7c3aed",
                }}
              >
                {totalGrossAmount.toFixed(2)}
              </div>
            </div>
            <div style={{ textAlign: "center" }}>
              <div style={{ fontSize: "12px", color: "#64748b" }}>Net Amount</div>
              <div
                style={{
                  fontSize: "18px",
                  fontWeight: "600",
                  color: "#7c3aed",
                }}
              >
                {totalNetAmount.toFixed(2)}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ------------- Main Container -------------

const SalesCustomerReturnReportHSN_1A = () => {
  const today = new Date();
  const [fromDate, setFromDate] = useState(today.toISOString().split("T")[0]);
  const [toDate, setToDate] = useState(today.toISOString().split("T")[0]);
  const [searchBy, setSearchBy] = useState("customerwise");
  const [reportType, setReportType] = useState("details");
  const [filters, setFilters] = useState({
    store: ["All Stores"],
    customer: ["All Customers"],
    group: ["All Group Names"],
    product: ["All Products"],
  });
  const [stores, setStores] = useState(["All Stores"]);
  const [customers, setCustomers] = useState(["All Customers"]);
  const [groups, setGroups] = useState(["All Group Names"]);
  const [products, setProducts] = useState(["All Products"]);
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showReport, setShowReport] = useState(false);
  const [branchProfile, setBranchProfile] = useState({
    branchName: "SUPREME TRADERS",
    address: "",
    phone: "",
    city: "",
  });
  const [salesReturnData, setSalesReturnData] = useState([]);
  const [productGroupMap, setProductGroupMap] = useState({});
  const [productCategoryMap, setProductCategoryMap] = useState({});
  const [productBrandMap, setProductBrandMap] = useState({});
  const [customerMap, setCustomerMap] = useState({});

  function encodeEmailForPath(email) {
    return email.replace(/[.#$\[\]]/g, "_");
  }

  // Firebase Auth and Data Loading
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setCurrentUser(user);
      if (user) {
        loadFirebaseData(user);
      }
      setLoading(false);
    });
    return () => unsubscribe();
    // eslint-disable-next-line
  }, []);

  function loadFirebaseData(user) {
    const encodedEmail = encodeEmailForPath(user.email);

    // Branch profile info
    const branchProfileRef = ref(database, `users/${encodedEmail}/branchProfiles`);
    onValue(branchProfileRef, (snapshot) => {
      const data = snapshot.val();
      if (data) {
        const profiles = Object.values(data);
        const latestProfile = profiles[profiles.length - 1];
        setBranchProfile({
          branchName: latestProfile.branchName || "SUPREME TRADERS",
          address: `${latestProfile.address1 || ""} ${latestProfile.address2 || ""}`.trim(),
          phone: latestProfile.mobileNo || "",
          city:
            latestProfile.city ||
            getCityFromAddress(
              `${latestProfile.address1 || ""} ${latestProfile.address2 || ""}`
            ),
        });
      }
    });

    // Stores
    const storesRef = ref(database, `users/${encodedEmail}/stores`);
    onValue(storesRef, (snapshot) => {
      const storesData = ["All Stores"];
      if (snapshot.exists()) {
        snapshot.forEach((childSnapshot) => {
          const store = childSnapshot.val();
          if (store.name && !storesData.includes(store.name)) {
            storesData.push(store.name);
          }
        });
      }
      setStores(storesData);
    });

    // Customers (including phone number / TIN mapping)
    const customersRef = ref(database, `users/${encodedEmail}/customers`);
    onValue(customersRef, (snapshot) => {
      const customersData = ["All Customers"];
      const customerMapObj = {};
      if (snapshot.exists()) {
        snapshot.forEach((childSnapshot) => {
          const customer = childSnapshot.val();
          if (customer.customerName && !customersData.includes(customer.customerName)) {
            customersData.push(customer.customerName);
          }
          if (customer.customerName) {
            customerMapObj[customer.customerName] = customer;
          }
        });
      }
      setCustomers(customersData);
      setCustomerMap(customerMapObj);
    });

    // Groups
    const groupsRef = ref(database, `users/${encodedEmail}/productGroups`);
    onValue(groupsRef, (snapshot) => {
      const groupsData = ["All Group Names"];
      if (snapshot.exists()) {
        snapshot.forEach((childSnapshot) => {
          const group = childSnapshot.val();
          if (group.name && !groupsData.includes(group.name)) {
            groupsData.push(group.name);
          }
        });
      }
      setGroups(groupsData);
    });

    // Products and their mappings
    const productsRef = ref(database, `users/${encodedEmail}/products`);
    onValue(productsRef, (snapshot) => {
      const productsData = ["All Products"];
      const groupMap = {};
      const categoryMap = {};
      const brandMap = {};
      if (snapshot.exists()) {
        snapshot.forEach((childSnapshot) => {
          const product = childSnapshot.val();
          if (product.productName && !productsData.includes(product.productName)) {
            productsData.push(product.productName);
            groupMap[product.productName] =
              product.groupName || product.group || "No Group Assigned";
            categoryMap[product.productName] = product.category || "Unknown Category";
            brandMap[product.productName] = product.brand || "Unknown Brand";
          }
        });
      }
      setProducts(productsData);
      setProductGroupMap(groupMap);
      setProductCategoryMap(categoryMap);
      setProductBrandMap(brandMap);
    });

  // Updated salesReturns data loading in loadFirebaseData
const salesReturnsRef = ref(database, `users/${encodedEmail}/salesReturns`);
onValue(salesReturnsRef, (snapshot) => {
  const data = snapshot.val();
  if (data) {
    const returnRecords = [];
    Object.entries(data).forEach(([returnId, returnData]) => {
      if (returnData.items && Array.isArray(returnData.items)) {
        returnData.items.forEach((item) => {
          if (item.itemDescription) {
            const returnQty = Number(item.returnQty) || 0;
            const mrp = Number(item.mrp) || 0;
            const discPercent = Number(item.disc) || 0;
            const cgstRate = Number(item.cgst) || 0;
            const sgstRate = Number(item.sgst) || 0;
            const grossAmount = returnQty * mrp;
            const discount = (grossAmount * discPercent) / 100;
            const taxableValue = grossAmount - discount;
            const cgstTax = (taxableValue * cgstRate) / 100;
            const sgstTax = (taxableValue * sgstRate) / 100;
            const netAmount = taxableValue + cgstTax + sgstTax;

            returnRecords.push({
              returnId,
              srnNo: returnData.srnNo || "N/A",
              srnDate: returnData.srnDate || "",
              billNo: returnData.billNo || "N/A",
              store: returnData.store || "Unknown Store",
              customer: returnData.customer || "Unknown Customer",
              phoneNumber: returnData.phone || returnData.phoneNumber || "",
              gstin: returnData.gstin || "N/A",
              tin: returnData.tin || "",
              itemDescription: item.itemDescription || "Unknown Product",
              groupName: productGroupMap[item.itemDescription] || "No Group Assigned",
              category: productCategoryMap[item.itemDescription] || "Unknown Category",
              brand: productBrandMap[item.itemDescription] || "Unknown Brand",
              barCode: item.barCode || "",
              uom: item.uom || "",
              hsnCode: item.hsnCode || "",
              qty: returnQty,
              mrp,
              grossAmount,
              discount,
              taxableValue,
              cgstTax,
              sgstTax,
              netAmount,
            });
          }
        });
      }
    });
    setSalesReturnData(returnRecords);
  } else {
    setSalesReturnData([]);
  }
});

    // Cleanup on unmount
    return () => {
      off(branchProfileRef);
      off(storesRef);
      off(customersRef);
      off(groupsRef);
      off(productsRef);
      off(salesReturnsRef);
    };
  }

  // Update group/category/brand mapping for items when maps change
  useEffect(() => {
    if (
      Object.keys(productGroupMap).length > 0 &&
      Object.keys(productCategoryMap).length > 0 &&
      Object.keys(productBrandMap).length > 0 &&
      salesReturnData.length > 0
    ) {
      setSalesReturnData((prevData) =>
        prevData.map((item) => ({
          ...item,
          groupName: productGroupMap[item.itemDescription] || "No Group Assigned",
          category: productCategoryMap[item.itemDescription] || "Unknown Category",
          brand: productBrandMap[item.itemDescription] || "Unknown Brand",
        }))
      );
    }
    // Do not add setSalesReturnData to deps to avoid infinite loop!
    // eslint-disable-next-line
  }, [productGroupMap, productCategoryMap, productBrandMap]);

  // --- UI handlers
  const handleFilterChange = (key, value) => {
    setFilters({ ...filters, [key]: value });
  };
  const handleGenerateReport = () => {
    setShowReport(true);
  };
  const handlePrint = () => {
    const printContent = (
      <SalesCustomerReturnReportHSNDisplay
        formData={{ fromDate, toDate, searchBy, reportType }}
        filters={filters}
        branchProfile={branchProfile}
        returnData={salesReturnData}
        customerMap={customerMap}
      />
    );
  
    const htmlString = ReactDOMServer.renderToString(printContent);
  
    const printWindow = window.open('', '', 'width=1200,height=800');
    printWindow.document.write(`
      <html>
        <head>
          <title>Sales Customer Return Report</title>
          <style>
            body { 
              margin: 0; 
              padding: 5px; 
              font-family: Arial, sans-serif; 
              font-size: 8px;
            }
            @page { 
              size: A4 landscape; 
              margin: 5mm; 
            }
            table { 
              page-break-inside: auto;
              width: 100%;
              font-size: 7px;
              table-layout: fixed;
              border-collapse: collapse;
            }
            tr { 
              page-break-inside: avoid; 
              page-break-after: auto; 
            }
            th, td {
              padding: 2px !important;
              font-size: 7px !important;
              white-space: nowrap;
              overflow: hidden;
              text-overflow: ellipsis;
              max-width: 60px;
            }
            /* Specific column widths */
            th:nth-child(1), td:nth-child(1) { width: 30px; } /* S.No */
            th:nth-child(2), td:nth-child(2) { width: 80px; } /* Customer Name */
            th:nth-child(3), td:nth-child(3) { width: 50px; } /* Phone Number */
            th:nth-child(4), td:nth-child(4) { width: 50px; } /* TIN */
            th:nth-child(5), td:nth-child(5) { width: 50px; } /* Inv.No */
            th:nth-child(6), td:nth-child(6) { width: 50px; } /* Inv.Date */
            th:nth-child(7), td:nth-child(7) { width: 50px; } /* GST Amount */
            th:nth-child(8), td:nth-child(8) { width: 50px; } /* Disc Amount */
            th:nth-child(9), td:nth-child(9) { width: 50px; } /* Gross Amount */
            th:nth-child(10), td:nth-child(10) { width: 60px; font-weight: bold; } /* Net Amount */
            .report-display {
              width: 93% !important;
              max-width: none !important;
              overflow: visible !important;
              border: 2px solid #3b82f6 !important;
              padding: 15px !important;
            }
            div[style*="overflowX"] {
              overflow: visible !important;
            }
          </style>
        </head>
        <body>
          <div id="print-content">${htmlString}</div>
          <script>
            setTimeout(function() {
              window.print();
              window.close();
            }, 1000);
          </script>
        </body>
      </html>
    `);
    printWindow.document.close();
  };
  const formDataObj = {
    fromDate,
    toDate,
    searchBy,
    reportType,
  };

  // --------- UI Render ---------
  if (loading) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "400px",
          fontSize: "18px",
          color: "#64748b",
          backgroundColor: "#f8fafc",
          borderRadius: "12px",
          border: "1px solid #e2e8f0",
        }}
      >
        <div style={{ textAlign: "center" }}>
          <div
            style={{
              width: "40px",
              height: "40px",
              border: "4px solid #e2e8f0",
              borderTop: "4px solid #3b82f6",
              borderRadius: "50%",
              animation: "spin 1s linear infinite",
              margin: "0 auto 16px",
            }}
          ></div>
          <div>Loading Return Data...</div>
        </div>
      </div>
    );
  }
  if (!currentUser) {
    return (
      <div
        style={{
          textAlign: "center",
          padding: "3rem",
          color: "#64748b",
          fontSize: "18px",
          backgroundColor: "#f8fafc",
          borderRadius: "12px",
          border: "1px solid #e2e8f0",
        }}
      >
        <h2 style={{ color: "#1e293b", marginBottom: "16px" }}>
          Authentication Required
        </h2>
        <p>Please login to access the Customerwise Return (GSTIN)_1A</p>
      </div>
    );
  }

  return (
    <div
      style={{
        width: "100%",
        maxWidth: "90rem",
        backgroundColor: "white",
        borderRadius: "16px",
        boxShadow:
          "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
        padding: "32px",
        margin: "0 auto",
        fontFamily: "system-ui, -apple-system, sans-serif",
        border: "1px solid #e2e8f0",
      }}
    >
      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
          @media print {
            body * {
              visibility: hidden;
            }
            .report-display, .report-display * {
              visibility: visible;
            }
            .report-display {
              position: absolute;
              left: 0;
              top: 0;
              width: 100%;
              margin: 0;
              padding: 20px;
              box-shadow: none;
              border: none;
            }
          }
        `}
      </style>

      {/* Main title */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "12px",
          marginBottom: "24px",
          paddingBottom: "16px",
          borderBottom: "2px solid #e2e8f0",
        }}
      >
        <div
          style={{
            width: "6px",
            height: "32px",
            backgroundColor: "#3b82f6",
            borderRadius: "3px",
          }}
        ></div>
        <h1
          style={{
            fontSize: "28px",
            fontWeight: "700",
            color: "#1e293b",
            margin: "0",
          }}
        >
         Customerwise Return (GSTIN)_1A
        </h1>
      </div>

      {/* Date Range and Search Filters */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
          gap: "20px",
          marginBottom: "32px",
          padding: "24px",
          backgroundColor: "#f8fafc",
          borderRadius: "12px",
          border: "1px solid #e2e8f0",
        }}
      >
        <div>
          <label
            style={{
              display: "block",
              fontSize: "14px",
              fontWeight: "600",
              color: "#374151",
              marginBottom: "8px",
            }}
          >
            From Date
          </label>
          <input
            type="date"
            value={fromDate}
            onChange={(e) => setFromDate(e.target.value)}
            style={{
              width: "100%",
              padding: "12px 16px",
              border: "1px solid #d1d5db",
              borderRadius: "8px",
              fontSize: "14px",
              outline: "none",
              transition: "all 0.2s",
              backgroundColor: "white",
              boxSizing: "border-box",
            }}
            onFocus={(e) => (e.target.style.borderColor = "#3b82f6")}
            onBlur={(e) => (e.target.style.borderColor = "#d1d5db")}
          />
        </div>
        <div>
          <label
            style={{
              display: "block",
              fontSize: "14px",
              fontWeight: "600",
              color: "#374151",
              marginBottom: "8px",
            }}
          >
            To Date
          </label>
          <input
            type="date"
            value={toDate}
            onChange={(e) => setToDate(e.target.value)}
            style={{
              width: "100%",
              padding: "12px 16px",
              border: "1px solid #d1d5db",
              borderRadius: "8px",
              fontSize: "14px",
              outline: "none",
              transition: "all 0.2s",
              backgroundColor: "white",
              boxSizing: "border-box",
            }}
            onFocus={(e) => (e.target.style.borderColor = "#3b82f6")}
            onBlur={(e) => (e.target.style.borderColor = "#d1d5db")}
          />
        </div>
        <div>
          <label
            style={{
              display: "block",
              fontSize: "14px",
              fontWeight: "600",
              color: "#374151",
              marginBottom: "8px",
            }}
          >
            Search By
          </label>
          <select
            value={searchBy}
            onChange={(e) => setSearchBy(e.target.value)}
            style={{
              width: "100%",
              padding: "12px 16px",
              border: "1px solid #d1d5db",
              borderRadius: "8px",
              fontSize: "14px",
              outline: "none",
              transition: "all 0.2s",
              backgroundColor: "white",
              cursor: "pointer",
              boxSizing: "border-box",
            }}
            onFocus={(e) => (e.target.style.borderColor = "#3b82f6")}
            onBlur={(e) => (e.target.style.borderColor = "#d1d5db")}
          >
            <option value="customerwise">Customerwise</option>
            <option value="groupwise">Groupwise</option>
            <option value="datewise">Datewise</option>
          </select>
        </div>
        <div>
          <label
            style={{
              display: "block",
              fontSize: "14px",
              fontWeight: "600",
              color: "#374151",
              marginBottom: "8px",
            }}
          >
            Report Type
          </label>
          <select
            value={reportType}
            onChange={(e) => setReportType(e.target.value)}
            style={{
              width: "100%",
              padding: "12px 16px",
              border: "1px solid #d1d5db",
              borderRadius: "8px",
              fontSize: "14px",
              outline: "none",
              transition: "all 0.2s",
              backgroundColor: "white",
              cursor: "pointer",
              boxSizing: "border-box",
            }}
            onFocus={(e) => (e.target.style.borderColor = "#3b82f6")}
            onBlur={(e) => (e.target.style.borderColor = "#d1d5db")}
          >
            <option value="details">Details</option>
            <option value="abstract">Abstract</option>
          </select>
        </div>
      </div>

      {/* Filter Sections */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))",
          gap: "20px",
          marginBottom: "32px",
        }}
      >
        {/* <FilterSection
          title="Stores"
          options={stores}
          selected={filters.store}
          onChange={(value) => handleFilterChange("store", value)}
        /> */}
        <FilterSection
          title="Customer"
          options={customers}
          selected={filters.customer}
          onChange={(value) => handleFilterChange("customer", value)}
        />
        <FilterSection
          title="Group Name"
          options={groups}
          selected={filters.group}
          onChange={(value) => handleFilterChange("group", value)}
        />
        <FilterSection
          title="Product"
          options={products}
          selected={filters.product}
          onChange={(value) => handleFilterChange("product", value)}
        />
      </div>

      {/* Action Buttons */}
      <div
        style={{
          display: "flex",
          gap: "16px",
          marginBottom: "32px",
          justifyContent: "center",
        }}
      >
        <button
          onClick={handleGenerateReport}
          style={{
            background: "linear-gradient(135deg, #16a34a 0%, #15803d 100%)",
            color: "white",
            padding: "12px 24px",
            borderRadius: "8px",
            border: "none",
            cursor: "pointer",
            transition: "all 0.3s",
            fontSize: "16px",
            fontWeight: "600",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            minWidth: "140px",
            boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = "translateY(-2px)";
            e.currentTarget.style.boxShadow = "0 6px 12px rgba(0, 0, 0, 0.15)";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = "translateY(0)";
            e.currentTarget.style.boxShadow = "0 4px 6px rgba(0, 0, 0, 0.1)";
          }}
        >
          Generate Report
        </button>
        <button
          onClick={handlePrint}
          style={{
            background: "linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)",
            color: "white",
            padding: "12px 24px",
            borderRadius: "8px",
            border: "none",
            cursor: "pointer",
            transition: "all 0.3s",
            fontSize: "16px",
            fontWeight: "600",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            minWidth: "120px",
            boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = "translateY(-2px)";
            e.currentTarget.style.boxShadow = "0 6px 12px rgba(0, 0, 0, 0.15)";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = "translateY(0)";
            e.currentTarget.style.boxShadow = "0 4px 6px rgba(0, 0, 0, 0.1)";
          }}
        >
          Print Report
        </button>
      </div>

      {/* Report Display */}
      {showReport && (
        <SalesCustomerReturnReportHSNDisplay
          formData={formDataObj}
          filters={filters}
          branchProfile={branchProfile}
          returnData={salesReturnData}
          customerMap={customerMap}
        />
      )}
    </div>
  );
};

export default SalesCustomerReturnReportHSN_1A;

